import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import {
  ContentLayout,
  HeaderBreadcrumbs,
  Loader,
  usePermission,
} from "../../../shared";
import InputControl from "../../../shared/components/InputControl";
import Avadar_img from "../../../assets/images/Icons/Avadar_img.png";
import { useAppSettings } from "../hooks";

export const EditAppSettings = () => {
  const {
    state,
    formValidator,
    handleInputChange,
    handleSwitchChange,
    handleColorChange,
    handleGoogleApiKeyChange,
    handleFirebaseChange,
    handleFirebaseSwitchChange,
    onFileChange,
    onUpdateAppSettings,
  } = useAppSettings();
  const isEditable = usePermission("settings-modify");
  const { details } = state;

  const colorFields = [
    { name: "colorPrimary", label: "Primary Colour" },
    { name: "colorPrimaryLight", label: "Primary Light Colour" },
    { name: "colorSecondary", label: "Secondary Colour" },
    { name: "colorAccent", label: "Accent Colour" },
  ];

  const imageUploader = ({ label, type, file, url, inputId }) => (
    <Stack gap="8px">
      <Typography variant="subtitle2" fontWeight={600}>
        {label}
      </Typography>
      <Stack direction="row" gap="10px" alignItems="center">
        <Avatar
          sx={{
            bgcolor: "#F2F2F2",
            width: "84px",
            height: "80px",
            borderRadius: "8px",
          }}
          variant="rounded"
        >
          <img
            width="50px"
            alt={label}
            src={file ? URL.createObjectURL(file) : url || Avadar_img}
          />
        </Avatar>
        <div>
          <input
            id={inputId}
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            onChange={(e) => onFileChange(e, type)}
            style={{ display: "none" }}
            disabled={!isEditable}
          />
          <label htmlFor={inputId}>
            <Button
              color="info"
              component="span"
              sx={{ mt: 2 }}
              disabled={!isEditable}
            >
              {url || file ? "Change" : "Upload"}
            </Button>
          </label>
        </div>
      </Stack>
      <Typography variant="body2" color="#808080">
        Upload an image with 1:1 ratio and less than 1MB.
      </Typography>
      <FormHelperText sx={{ color: "#C60808" }}>
        {formValidator.current.message(label, file, "ImageSize|ImageFormat")}
      </FormHelperText>
    </Stack>
  );

  const firebaseSection = (platform, label) => {
    const data = details.firebase_data[platform];
    return (
      <Card sx={{ p: "32px 24px", mb: "24px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ width: "150px" }}
            >
              {label}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack gap="16px">
              <InputControl
                name="store_url"
                label="Store URL"
                value={data.store_url ?? ""}
                onChange={(e) => handleFirebaseChange(platform, e)}
                disabled={!isEditable}
              />
              <Stack direction="row" gap="16px">
                <InputControl
                  name="store_version"
                  label="Store Version"
                  value={data.store_version ?? ""}
                  onChange={(e) => handleFirebaseChange(platform, e)}
                  disabled={!isEditable}
                />
                <InputControl
                  name="force_update_current_version"
                  label="Force Update Current Version"
                  value={data.force_update_current_version ?? ""}
                  onChange={(e) => handleFirebaseChange(platform, e)}
                  disabled={!isEditable}
                />
              </Stack>
              <InputControl
                name="title"
                label="Title"
                value={data.title ?? ""}
                onChange={(e) => handleFirebaseChange(platform, e)}
                disabled={!isEditable}
              />
              <InputControl
                type="textarea"
                name="message"
                label="Message"
                value={data.message ?? ""}
                onChange={(e) => handleFirebaseChange(platform, e)}
                disabled={!isEditable}
              />
              <InputControl
                type="switch"
                label="Force Update Required"
                value={!!data.force_update_required}
                onChange={(e) => handleFirebaseSwitchChange(platform, e)}
                disabled={!isEditable}
              />
            </Stack>
          </Grid>
        </Grid>
      </Card>
    );
  };

  return (
    <ContentLayout
      title="App Settings"
      breadcrumbs={
        <HeaderBreadcrumbs
          links={[{ name: "Dashboard", href: "/" }, { name: "App Settings" }]}
        />
      }
      rightContent={
        isEditable && (
          <Button
            variant="contained"
            color="primary"
            onClick={onUpdateAppSettings}
            disabled={state.isSaveButtonBusy || state.isSaveButton}
          >
            {state.isSaveButtonBusy ? "Saving..." : "Save"}
          </Button>
        )
      }
    >
      {state.isLoading ? (
        <Loader />
      ) : (
        <>
          {formValidator.current.purgeFields()}
          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  General
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack gap="16px">
                  <InputControl
                    name="name"
                    label="App Name"
                    value={details.name ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "App Name",
                      details.name,
                      "required"
                    )}
                    helperText={formValidator.current.message(
                      "App Name",
                      details.name,
                      "required"
                    )}
                  />
                  <InputControl
                    type="textarea"
                    name="address"
                    label="Address"
                    value={details.address ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                  />
                  <Stack direction="row" gap="16px">
                    <InputControl
                      name="country_code"
                      label="Country Code"
                      value={details.country_code ?? ""}
                      onChange={handleInputChange}
                      disabled={!isEditable}
                    />
                    <InputControl
                      name="currency_code"
                      label="Currency Code"
                      value={details.currency_code ?? ""}
                      onChange={handleInputChange}
                      disabled={!isEditable}
                    />
                  </Stack>
                  <InputControl
                    name="loyalty_term"
                    label="Loyalty Term"
                    value={details.loyalty_term ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Branding
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack gap="24px">
                  {imageUploader({
                    label: "Logo",
                    type: "logo",
                    file: state.logoFile,
                    url: details.logo,
                    inputId: "logo-upload",
                  })}
                  {imageUploader({
                    label: "App Icon",
                    type: "app_icon",
                    file: state.appIconFile,
                    url: details.app_icon,
                    inputId: "app-icon-upload",
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Theme Colours
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Grid container spacing={2}>
                  {colorFields.map((field) => (
                    <Grid item xs={12} sm={6} key={field.name}>
                      <InputControl
                        name={field.name}
                        label={field.label}
                        value={details.colors[field.name] ?? ""}
                        onChange={handleColorChange}
                        disabled={!isEditable}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                sx={{
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "4px",
                                  border: "1px solid #E6E6E6",
                                  bgcolor:
                                    details.colors[field.name] || "#FFFFFF",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Contact & Social
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack gap="16px">
                  <Stack direction="row" gap="16px">
                    <InputControl
                      name="phone_number"
                      label="Phone Number"
                      value={details.phone_number ?? ""}
                      onChange={handleInputChange}
                      disabled={!isEditable}
                    />
                    <InputControl
                      name="display_email"
                      label="Display Email"
                      value={details.display_email ?? ""}
                      onChange={handleInputChange}
                      disabled={!isEditable}
                      error={formValidator.current.message(
                        "Display Email",
                        details.display_email,
                        "email"
                      )}
                      helperText={formValidator.current.message(
                        "Display Email",
                        details.display_email,
                        "email"
                      )}
                    />
                  </Stack>
                  <InputControl
                    name="facebook_url"
                    label="Facebook URL"
                    value={details.facebook_url ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "Facebook URL",
                      details.facebook_url,
                      "url"
                    )}
                    helperText={formValidator.current.message(
                      "Facebook URL",
                      details.facebook_url,
                      "url"
                    )}
                  />
                  <InputControl
                    name="instagram_url"
                    label="Instagram URL"
                    value={details.instagram_url ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "Instagram URL",
                      details.instagram_url,
                      "url"
                    )}
                    helperText={formValidator.current.message(
                      "Instagram URL",
                      details.instagram_url,
                      "url"
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Email Configuration
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack direction="row" gap="16px">
                  <InputControl
                    name="reply_to_address"
                    label="Reply To Address"
                    value={details.reply_to_address ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "Reply To Address",
                      details.reply_to_address,
                      "email"
                    )}
                    helperText={formValidator.current.message(
                      "Reply To Address",
                      details.reply_to_address,
                      "email"
                    )}
                  />
                  <InputControl
                    name="config_mail"
                    label="Config Mail"
                    value={details.config_mail ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "Config Mail",
                      details.config_mail,
                      "email"
                    )}
                    helperText={formValidator.current.message(
                      "Config Mail",
                      details.config_mail,
                      "email"
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Links
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack gap="16px">
                  <InputControl
                    name="terms_url"
                    label="Terms URL"
                    value={details.terms_url ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "Terms URL",
                      details.terms_url,
                      "url"
                    )}
                    helperText={formValidator.current.message(
                      "Terms URL",
                      details.terms_url,
                      "url"
                    )}
                  />
                  <InputControl
                    name="privacy_policy_url"
                    label="Privacy Policy URL"
                    value={details.privacy_policy_url ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "Privacy Policy URL",
                      details.privacy_policy_url,
                      "url"
                    )}
                    helperText={formValidator.current.message(
                      "Privacy Policy URL",
                      details.privacy_policy_url,
                      "url"
                    )}
                  />
                  <InputControl
                    name="help_url"
                    label="Help URL"
                    value={details.help_url ?? ""}
                    onChange={handleInputChange}
                    disabled={!isEditable}
                    error={formValidator.current.message(
                      "Help URL",
                      details.help_url,
                      "url"
                    )}
                    helperText={formValidator.current.message(
                      "Help URL",
                      details.help_url,
                      "url"
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Payment
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <InputControl
                  name="stripe_publishable_key"
                  label="Stripe Publishable Key"
                  value={details.stripe_publishable_key ?? ""}
                  onChange={handleInputChange}
                  disabled={!isEditable}
                />
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Google API Keys
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack gap="16px">
                  <InputControl
                    name="web"
                    label="Web Google API Key"
                    value={details.google_api_key.web ?? ""}
                    onChange={handleGoogleApiKeyChange}
                    disabled={!isEditable}
                  />
                  <InputControl
                    name="ios"
                    label="iOS Google API Key"
                    value={details.google_api_key.ios ?? ""}
                    onChange={handleGoogleApiKeyChange}
                    disabled={!isEditable}
                  />
                  <InputControl
                    name="android"
                    label="Android Google API Key"
                    value={details.google_api_key.android ?? ""}
                    onChange={handleGoogleApiKeyChange}
                    disabled={!isEditable}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: "32px 24px", mb: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Features
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack gap="16px">
                  <Stack>
                    <InputControl
                      type="switch"
                      label="Reviews & Ratings"
                      value={!!details.review_configurable}
                      onChange={(e) =>
                        handleSwitchChange(e, "review_configurable")
                      }
                      disabled={!isEditable}
                    />
                    <Typography variant="body2" color="#808080">
                      Enable reviews and ratings in the client app.
                    </Typography>
                  </Stack>
                  <Stack>
                    <InputControl
                      type="switch"
                      label="VIP Text"
                      value={!!details.banjos_vip_text_configurable}
                      onChange={(e) =>
                        handleSwitchChange(e, "banjos_vip_text_configurable")
                      }
                      disabled={!isEditable}
                    />
                    <Typography variant="body2" color="#808080">
                      Show the VIP text in the client app.
                    </Typography>
                  </Stack>
                  <Stack>
                    <InputControl
                      type="switch"
                      label="Login OTP"
                      value={!!details.login_otp_configurable}
                      onChange={(e) =>
                        handleSwitchChange(e, "login_otp_configurable")
                      }
                      disabled={!isEditable}
                    />
                    <Typography variant="body2" color="#808080">
                      Require OTP verification when logging in to the client
                      app.
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Card>

          {firebaseSection("ios", "iOS App Version")}
          {firebaseSection("android", "Android App Version")}
        </>
      )}
    </ContentLayout>
  );
};
